import { Col } from 'react-styled-flexboxgrid'
import styled, { css } from 'styled-components'

import { Bold, CardCount, Color, Dashed, Spaces } from './interface'

const getCardBoxChildCSS = (cardCount: number) =>
  [...Array(cardCount).keys()].map((index: number) => {
    const count = index + 1
    return css`
      &:nth-child(${count}) {
        ${!index &&
        css`
          padding-top: 3px;
        `}
        padding-left: ${({ theme }) => theme.space.xxs};
        padding-right: ${({ theme }) => theme.space.xxs};
        width: ${`${100 - (cardCount - count) * 3}%`};
        height: ${!index ? '96%' : '100%'};
        z-index: ${cardCount - index};

        @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
          padding-left: ${({ theme }) => theme.space.sm};
          padding-right: ${({ theme }) => theme.space.sm};
          width: ${`${100 - (cardCount - count)}%`};
        }
      }
    `
  })

export const CardBox = styled.div`
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => `5px ${theme.space.sm}`};
  position: relative;
  z-index: 1;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    padding: 3px 22px;
  }
`

export const CardContent = styled.div<CardCount>`
  position: relative;
  width: 100%;
  height: 100%;

  ${({ cardCount }) =>
    cardCount > 1 &&
    css`
      ${CardBox} {
        position: absolute;
        padding-top: ${({ theme }) => theme.space.sm};
        padding-left: ${({ theme }) => theme.space.xs};

        & > * {
          box-shadow: 2px 2px 4px 0 rgba(0, 0, 0, 0.2);
        }

        ${getCardBoxChildCSS(cardCount)}
      }
    `}
`

export const Time = styled.div<Bold>`
  color: ${({ theme }) => theme.palette.blueGray80};
  margin-top: ${({ theme }) => `-${theme.space.lg}`};
  position: absolute;

  ${({ bold }) =>
    bold &&
    css`
      font-weight: ${({ theme }) => theme.fontWeight.bold};
      color: ${({ theme }) => theme.colors.brandSecondaryContrast};
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-top: -6px;
  }
`

export const TimelineItemBox = styled.div<Color & Spaces>`
  position: absolute;
  width: 100%;
  // $ContentHeight * (1 per 15min in mockups) + (margin/padding/border) * (-1 per 15min in mockups)
  height: ${({ spaces }) => `calc(150px * ${spaces} + 4px * ${spaces ? spaces - 1 : 0})`};
  margin-left: ${({ theme }) => theme.space.xxs};

  ${({ color }) =>
    color === 'info' &&
    css`
      background-color: ${({ theme }) => theme.palette.info20};
    `}

  ${({ color }) =>
    color === 'warning' &&
    css`
      background-color: ${({ theme }) => theme.palette.warning20};
    `}

  ${({ color }) =>
    color === 'success' &&
    css`
      background-color: ${({ theme }) => theme.palette.green20};
    `}

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    height: ${({ spaces }) => `calc(150px * ${spaces} + 4px * ${spaces ? spaces - 1 : 0})`};
  }
`

export const Content = styled.div`
  min-height: 150px;
  margin-left: ${({ theme }) => theme.space.md};
  position: relative;

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    min-height: 150px;
  }
`

export const Line = styled.div<Dashed>`
  border: 2px ${({ theme, dashed }) => `${dashed ? 'dashed' : 'solid'} ${theme.palette.blue20}`};
  width: 100%;
  position: relative;
  z-index: 0.5;
`

export const Dot = styled.div`
  width: ${({ theme }) => theme.layout.xxs};
  height: ${({ theme }) => theme.layout.xxs};
  background-color: ${({ theme }) => theme.palette.blue20};
  border-radius: ${({ theme }) => theme.layout.xxxl};
  position: absolute;
`

export const Inline = styled.div`
  display: flex;
  align-items: center;
`

export const TimeNowLine = styled.div`
  position: absolute;
  z-index: 5;
  border: 1px solid;
  margin-top: 9px;
  width: 100%;
  border: 3px solid ${({ theme }) => theme.colors.brandPrimary};

  &:nth-child(2) {
    z-index: 0;
  }

  &:last-child {
    opacity: 0.3;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-top: 12px;
  }
`

export const TimeNow = styled.div<{ minutes: number }>`
  position: absolute;
  margin-left: ${({ theme }) => `-${theme.space.xl}`};
  width: ${({ theme }) => `calc(100% + ${theme.space.xl})`};
  height: 100%;
  margin-top: -14px;

  ${Time} {
    font-size: ${({ theme }) => theme.fontSize.xs};
    background-color: ${({ theme }) => theme.colors.brandPrimary};
    padding: ${({ theme }) => theme.space.xxs};
    color: ${({ theme }) => theme.colors.brandPrimaryContrast};
    border-radius: 16px;
    z-index: 6;
    margin-top: 0;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-left: ${({ theme }) => `-${theme.space.lg}`};
    width: ${({ theme }) => `calc(100% + ${theme.space.lg})`};
    margin-top: calc(-17px + ${({ minutes }) => `${minutes * 5}px`});

    ${Time} {
      margin-left: ${({ theme }) => `-${theme.space.xl}`};
      padding: ${({ theme }) => `${theme.space.xs} ${theme.space.sm} 6px`};
    }
  }
`

export const TimeCol = styled(Col)`
  font-size: ${({ theme }) => theme.fontSize.xxs};
  color: ${({ theme }) => theme.palette.blueGray80};
  padding-left: ${({ theme }) => theme.space.md};
`

export const LineCol = styled(Col)`
  position: relative;
`
